import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LoginFormSteps } from "./steps/StepModel";
import { RootState } from "setup/redux/RootReducer";
import { IAuthenticationService } from "services/AuthenticationService";
import { container } from "setup/dependency/inversify.config";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { actions } from "../../redux/AuthRedux";
import { TwoFactorAuthenticationTypes } from "global/enums";
import { toast } from "react-toastify";
import { setGlobalSettings } from "app/modules/system/settings/ClientPortalSettingsSlice";
import { IClientPortalCPLSettingsService } from "services/ClientPortalCPLSettingsService";

enum ValidationMessages {
  InvalidUserPass = "Incorrect email address or password",
  InvalidOTP = "Invalid one time password",
}

type LoginPageState = {
  Loading: boolean;
  CurrentStep: LoginFormSteps;
  TwoFactorAuthType: TwoFactorAuthenticationTypes | undefined;
  Validated: boolean;
  Status: string;
};

const initialState: LoginPageState = {
  Loading: false,
  CurrentStep: LoginFormSteps.EmailAddress,
  TwoFactorAuthType: TwoFactorAuthenticationTypes.None,
  Validated: false,
  Status: "",
};

export const loginPageSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.Loading = action.payload;
    },
    setStatus: (state, action) => {
      state.Status = action.payload;
    },
    setTwoFactorAuthType: (state, action) => {
      state.TwoFactorAuthType = action.payload;
    },
    incrementCurrentStep: (state) => {
      state.CurrentStep += 1;
      state.Status = "";
    },
    decrementCurrentStep: (state) => {
      if (state.CurrentStep > 0) {
        state.CurrentStep -= 1;
        state.Status = "";
      }
    },
    invalidateUser: (state) => {
      state.Validated = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validateUser.pending, (state) => {
        state.Loading = true;
      })
      .addCase(validateUser.fulfilled, (state, action) => {
        const validated = action.payload;
        if (validated) {
          state.Validated = true;
          state.CurrentStep += 1;
          state.Status = "";
        } else {
          state.Validated = false;
          state.Status = ValidationMessages.InvalidUserPass;
        }
        state.Loading = false;
      })
      .addCase(validateUser.rejected, (state) => {
        state.Validated = false;
        state.Status = ValidationMessages.InvalidUserPass;
        state.Loading = false;
      });
  },
});

export const validateUser = createAsyncThunk<boolean, { emailAddress: string; password: string }, { state: RootState }>(
  "LoginPage/ValidateUser",
  async ({ emailAddress, password }, thunkAPI) => {
    const state = thunkAPI.getState();
    const authenticationService = container.get<IAuthenticationService>(SERVICE_TYPES.AuthenticationService);
    if (state.login.Validated) {
      return true;
    }

    const twoFactorAuthModel = await authenticationService.validateUser(
      state.companyProvider.companyId ?? "",
      emailAddress,
      password
    );

    // User/pass validation failed
    if (!twoFactorAuthModel) {
      thunkAPI.dispatch(setStatus(ValidationMessages.InvalidUserPass));
      return false;
    }

    thunkAPI.dispatch(setTwoFactorAuthType(twoFactorAuthModel.twoFactorAuthTypeID ?? 0));

    // User/pass validation successful and no 2FA setup
    if (twoFactorAuthModel.twoFactorAuthTypeID === TwoFactorAuthenticationTypes.None) {
      const loginSuccess = await new Promise<boolean>((resolve) => {
        thunkAPI.dispatch(
          actions.login(
            { companyID: state.companyProvider.companyId ?? "", emailAddress, password, oneTimePassword: undefined },
            {
              successHandler: () => {
                resolve(true);
              },
              errorHandler: () => {
                resolve(false);
              },
            }
          )
        );
      });
      return loginSuccess;
    }

    return true;
  }
);

// This intentionally does not have reducer cases as it's not async
export const verifyUser = createAsyncThunk<
  void,
  { emailAddress: string; password: string; oneTimePassword: string },
  { state: RootState }
>("LoginPage/VerifyUser", ({ emailAddress, password, oneTimePassword }, thunkAPI) => {
  thunkAPI.dispatch(setLoading(true));
  const state = thunkAPI.getState();
  thunkAPI.dispatch(
    actions.login(
      { companyID: state.companyProvider.companyId ?? "", emailAddress, password, oneTimePassword },
      {
        errorHandler: () => {
          thunkAPI.dispatch(setStatus(ValidationMessages.InvalidOTP));
        },
        successHandler: async () => {
          const clientPortalCPLSettingsService = container.get<IClientPortalCPLSettingsService>(
            SERVICE_TYPES.ClientPortalCPLSettingsService
          );
          const globalSettings = await clientPortalCPLSettingsService.getGlobalClientPortalSettings();
          thunkAPI.dispatch(setGlobalSettings(globalSettings));
        },
        finallyHandler: () => {
          thunkAPI.dispatch(setLoading(false));
        },
      }
    )
  );
});

export const requestNewVerificationCode = createAsyncThunk<
  void,
  { emailAddress: string; password: string },
  { state: RootState }
>("LoginPage/RequestNewVerificationCode", async ({ emailAddress, password }, thunkAPI) => {
  const state = thunkAPI.getState();
  const authenticationService = container.get<IAuthenticationService>(SERVICE_TYPES.AuthenticationService);
  await authenticationService.requestNewVerificationCode(state.companyProvider.companyId ?? "", emailAddress, password);
  toast.success("New verification code sent!");
});

export const {
  setLoading,
  incrementCurrentStep,
  setTwoFactorAuthType,
  decrementCurrentStep,
  setStatus,
  invalidateUser,
} = loginPageSlice.actions;

export const selectStatus = (state: RootState) => state.login.Status;
export const selectLoading = (state: RootState) => state.login.Loading;
export const selectCurrentStep = (state: RootState) => state.login.CurrentStep;
export const selectUserValidated = (state: RootState) => state.login.Validated;
export const selectTwoFactorAuthType = (state: RootState) => state.login.TwoFactorAuthType;

export default loginPageSlice.reducer;
